import React, { FC } from 'react'
import { Tr } from '../../../../_metronic/i18n/Tr'
import type { PlacementOption } from './AddAdvertisement.constants'
import { useNavigate } from 'react-router-dom'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'

interface PlacementSelection {
  name: string
  id: string
}

interface AddAdvertisementPlacementTabProps {
  adPlacementOptions: readonly PlacementOption[]
  selectedPlacement: string
  placementError: string
  isLoading: boolean
  loadError: string
  onSelectPlacement: (placement: PlacementSelection) => void
  onNext: () => void
}

const AddAdvertisementPlacementTab: FC<AddAdvertisementPlacementTabProps> = ({
  adPlacementOptions,
  selectedPlacement,
  placementError,
  isLoading,
  loadError,
  onSelectPlacement,
  onNext,
}) => {
  const t = Tr()
  const navigate  = useNavigate();
  const lang = useLang()
  const hasPlacements = adPlacementOptions.length > 0

  // English lives in the unprefixed field; uz/ru use `uz_` / `ru_` prefixed fields.
  const getLocalized = (option: PlacementOption, field: 'placementName' | 'description') =>
    (lang !== 'en' && (option as any)[`${lang}_${field}`]) || option[field]

  return (
    <>
      <div className='card card-flush py-4'>
        <div className='card-header d-flex align-items-center'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <rect x='2' y='3' width='20' height='14' rx='2' stroke='#009ef7' strokeWidth='1.8' />
                <path d='M8 21h8M12 17v4' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                <path d='M7 8h10M7 11h6' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Select Ad Placement')}</h2>
              <p className='text-muted fs-7 mb-0'>{t('Choose where you want your ad to appear')}</p>
            </div>
          </div>
        </div>
        <div className='card-body pt-4'>
          {isLoading ? (
            <div className='d-flex align-items-center justify-content-center py-10'>
              <div className='text-muted fs-6'>{t('Loading')}...</div>
            </div>
          ) : hasPlacements ? (
            <div className='row g-4'>
              {adPlacementOptions.map((option) => {
                const isSelected = selectedPlacement === option.placementName
                return (
                  <div className='col-md-6' key={option.adssettingId}>
                    <div
                      onClick={() => onSelectPlacement({ name: option.placementName, id: option.adssettingId.toString() })}
                      style={{
                        border: isSelected ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
                        borderRadius: '10px',
                        padding: '18px 20px',
                        cursor: 'pointer',
                        background: isSelected ? '#f0f9ff' : '#fff',
                        transition: 'all 0.18s ease',
                        boxShadow: isSelected ? '0 2px 12px rgba(0,158,247,0.13)' : '0 1px 4px rgba(0,0,0,0.04)',
                        position: 'relative',
                        minHeight: '100%',
                      }}
                    >
                      {isSelected && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '12px',
                            right: '14px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: '#009ef7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <svg width='11' height='8' viewBox='0 0 11 8' fill='none'>
                            <path d='M1 4L4 7L10 1' stroke='#fff' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                          </svg>
                        </span>
                      )}
                      <div className='d-flex justify-content-between align-items-start mb-2'>
                        <div style={{ paddingRight: '28px' }}>
                          <div className='fw-bold text-gray-800 fs-6 mb-1'>{getLocalized(option, 'placementName')}</div>
                          <div className='text-muted fs-7'>{getLocalized(option, 'description')}</div>
                          {option.maxAds > 0 && (
                            <div className='text-muted fs-8 mt-2'>
                              {option.maxAds} {option.maxAds === 1 ? t('Ad Slot') : t('Ad Slots')}
                            </div>
                          )}
                        </div>
                        <div className='d-flex justify-content-between align-items-end mt-5'>
                          <div className='text-end'>
                            <span className='fw-bold text-gray-900' style={{ fontSize: '15px' }}>
                              {option.amountPerDay.toLocaleString('en-US')}{' '}
                              <span className='text-muted fw-normal fs-7'>{t('UZS/day')}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className='text-center py-10'>
              <div className='fw-bold text-gray-800 fs-4 mb-2'>{t('No Data Found')}</div>
              <div className='text-muted fs-6'>
                {loadError ? t(loadError) : t('No placements are available right now.')}
              </div>
            </div>
          )}

          {placementError && (
            <div className='text-danger mt-3'>{placementError}</div>
          )}
        </div>
      </div>
      <div className='d-flex justify-content-end mt-5 gap-5'>
          <button type='button' className='btn btn-secondary' onClick={() => navigate("/viewbanneradvertisment")} >
            {t('Back')}
          </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={onNext}
          disabled={isLoading || !hasPlacements}
        >
          {t('Next')}
        </button>
      </div>
    </>
  )
}

export default AddAdvertisementPlacementTab
